import { customStyle, REGEX } from "components/Common/constants";
import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  InputGroup,
  InputGroupText,
  FormGroup,
  Label,
  InputGroupAddon,
  CardHeader,
  CardFooter,
  UncontrolledTooltip,
  Badge,
} from "reactstrap";
import Select, { components } from "react-select";
import { useDispatch, useSelector } from "react-redux";
import RightPanelCard from "components/Common/RightPanelCard";
import CommonTable from "components/ReactTable/Table";
import {
  DEFAULT_RECORDS_PER_PAGE,
  statusOptions,
  fixedWidthCustomStyle,
} from "components/Common/constants";
import {
  getSubUserList,
  addSubUser,
  getSubUserByID,
  updateSubUser,
} from "actions/subUser";
import { usersList } from "constants/users";
import { showAlert } from "components/Common/commonFunctions";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";

const initialFields = {
  firstName: {
    value: "",
    error: "",
  },
  lastName: {
    value: "",
    error: "",
  },
  email: {
    value: "",
    error: "",
  },
  contact: {
    value: "",
    error: "",
  },
  password: {
    value: "",
    error: "",
  },
  status: {
    value: "ACTIVE",
    error: "",
  },
};
function SubUsers({ customerId, notify, hasAnyError, activeTab }) {
  const dispatch = useDispatch();
  const subUserListData = useSelector((state) => state.users.usersList);

  const [subUserFormData, setSubUsetFormData] = useState(initialFields);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    mode: "",
    data: null,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [subUserListState, setSubUserListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
  });
  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchStatus: null,
    filterParams: [],
  });

  const formattedSubUserList = subUserListData?.data?.user?.map(
    (item, index) => ({
      ...item,
      srNo:
        (pageNum - 1) *
        (subUserListData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    }),
  );

  useEffect(() => {
    if (!customerId || activeTab !== "subUsers") return;
    //get the sub users listing data
    getSubUserList(dispatch, customerId, pageNum, filterState.filterParams);
    return () => {
      dispatch({ type: usersList, payload: {} });
    };
  }, [customerId, activeTab, pageNum, filterState.filterParams]);

  //to check the sub-userList response and till the time show loader or error as per response
  useEffect(() => {
    if (subUserListData?.code === 200) {
      setSubUserListState({
        isLoading: false,
        error: "",
        noRecords: subUserListData?.data?.user?.length === 0,
      });
      setShowLoaderOnClick(false);
    } else if (subUserListData?.code || subUserListData?.message) {
      setSubUserListState({
        isLoading: false,
        error:
          subUserListData?.message ||
          "Failed to load sub user list! Please try again.",
        noRecords: false,
      });
      setShowLoaderOnClick(false);
    }
  }, [subUserListData]);

  // based on the field name will retuen error if condition meets
  const getValidationError = (name, value) => {
    switch (name) {
      case "firstName":
        return !value
          ? "Please enter first name"
          : value.length < 2
            ? "First name must be at least 2 characters"
            : value.length > 50
              ? "First name must be less than 50 characters"
              : "";

      case "lastName":
        return !value
          ? "Please enter last name"
          : value.length < 2
            ? "Last name must be at least 2 characters"
            : value.length > 50
              ? "Last name must be less than 50 characters"
              : "";

      case "email":
        return !value
          ? "Please enter email address"
          : value.length > 100
            ? "Email must be less than 100 characters"
            : !REGEX.EMAIL_REGEX.test(value)
              ? "Please enter a valid email address"
              : "";

      case "contact":
        return !value
          ? "Please enter contact number"
          : !REGEX.CONTACT_REGEX.test(value)
            ? "Please enter a valid 9-digit contact number"
            : "";

      case "password":
        return !value
          ? "Please enter a password"
          : value.length < 8
            ? "The password must contain 8 characters"
            : "";

      default:
        return "";
    }
  };

  //to validate form for all the keys in the userForm object
  const validateForm = () => {
    let newSubUser = { ...subUserFormData };

    Object.keys(newSubUser).forEach((key) => {
      // Skip password validation in Edit mode
      if (modal.mode === "Edit" && key === "password") {
        newSubUser[key] = {
          ...newSubUser[key],
          error: "",
        };
        return;
      }
      newSubUser[key] = {
        ...newSubUser[key],
        error: getValidationError(key, newSubUser[key].value),
      };
    });
    setSubUsetFormData(newSubUser);
    return Object.values(newSubUser).every((item) => item.error === "");
  };

  const handleSaveClick = async () => {
    if (!validateForm()) return;
    try {
      setShowLoaderOnClick(true);

      let payload = {};

      Object.keys(subUserFormData).forEach((key) => {
        if (modal.mode === "Edit" && key === "password") return;

        if (
          modal.mode !== "Edit" ||
          subUserFormData[key].value !== subUserFormData[key].original
        ) {
          if (key === "contact") {
            payload.phone = subUserFormData[key].value.trim();
          } else {
            payload[key] = subUserFormData[key].value.trim();
          }
        }
      });

      if (modal.mode === "Edit" && !Object.keys(payload).length) {
        notify("info", "No changes detected");
        return;
      }
      payload.customerID = customerId;
      payload.roleID = 5;
      // Password is required only while adding
      if (modal.mode !== "Edit") {
        payload.password = subUserFormData.password.value;
      }
      const res =
        modal.mode === "Edit"
          ? await updateSubUser(modal.data.ID, payload)
          : await addSubUser(payload);

      if (res?.code === 200) {
        notify(
          "success",
          res?.message ||
          (modal.mode === "Edit"
            ? "Sub user updated successfully"
            : "Sub user added successfully"),
        );

        handleCloseModal();

        if (pageNum === 1) {
          getSubUserList(
            dispatch,
            customerId,
            pageNum,
            filterState.filterParams,
          );
        } else {
          setPageNum(1);
        }
      } else {
        const message = Array.isArray(res?.message)
          ? res.message[0]?.Msg
          : res?.message ||
          (modal.mode === "Edit"
            ? "Failed to update sub user!"
            : "Failed to add sub user!");

        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        modal.mode === "Edit"
          ? "Failed to update sub user!"
          : "Failed to add sub user!",
      );
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setSubUsetFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleCloseModal = () => {
    setModal({ isOpen: false, mode: "", data: null });
    setSubUsetFormData(initialFields);
  };

  const handleInputChange = (fieldName, newValue) => {
    setSubUsetFormData((prev) => ({
      ...prev,
      [fieldName]: {
        value: newValue,
        error: "",
      },
    }));
  };

  const columns = [
    { Header: "Sr. no.", accessor: "srNo", className: "col-srNo" },
    {
      Header: "Name",
      accessor: "firstName",
      className: "col-name",
      Cell: ({ row }) =>
        `${row.original.firstName || ""} ${row.original.lastName || ""}`.trim(),
    },
    {
      Header: "Email",
      accessor: "email",
      className: "customer-subUser-name",
    },
    {
      Header: "Contact Number",
      accessor: "phone",
      className: "customer-subUser-contact",
    },
    {
      Header: "Status",
      accessor: "status",
      className: "customer-subUser-status",
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value === "SUSPENDED" ? "Suspended" : value}
        </Badge>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      className: "customer-subUser-action",
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="actions-right d-flex justify-content-start">
          <div>
            <Button
              className="btn-icon icon-info"
              color="link"
              id={`edit${row.original.ID}`}
              size="sm"
              onClick={async (e) => {
                e.stopPropagation();
                await handleOpenEditSubUser(row.original);
              }}
            >
              <i className="tim-icons icon-pencil tim-md" />
            </Button>
            <UncontrolledTooltip delay={0} target={`edit${row.original.ID}`}>
              Edit
            </UncontrolledTooltip>

            <span className="mx-1 text-muted">|</span>
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleStatusChange(row.original);
              }}
              color="link"
              size="sm"
              className="btn-icon btn-neutral ml-1"
              id={`status${row.original.ID}`}
            >
              {row.original.status === "ACTIVE" ? (
                <i className="fa fa-xmark custom-icon btn-icon icon-danger" />
              ) : (
                <i className="fa fa-check custom-icon btn-icon icon-success" />
              )}
            </Button>
            <UncontrolledTooltip delay={0} target={`status${row.original.ID}`}>
              {row.original.status === "ACTIVE" ? "Suspend" : "Activate"}
            </UncontrolledTooltip>
          </div>
        </div>
      ),
    },
  ];

  const handleOpenEditSubUser = async (row) => {
    setShowLoaderOnClick(true);
    try {
      const res = await getSubUserByID(row.ID, customerId);

      if (res?.code === 200) {
        const data = res.data;

        setSubUsetFormData({
          firstName: {
            value: data.firstName || "",
            original: data.firstName || "",
            error: "",
          },
          lastName: {
            value: data.lastName || "",
            original: data.lastName || "",
            error: "",
          },
          email: {
            value: data.email || "",
            original: data.email || "",
            error: "",
          },
          contact: {
            value: data.phone || "",
            original: data.phone || "",
            error: "",
          },
          password: {
            value: "",
            original: "",
            error: "",
          },
          status: {
            value: data.status || "ACTIVE",
            original: data.status || "ACTIVE",
            error: "",
          },
        });

        handleOpenModal("Edit", data);
      } else {
        notify("danger", res?.message || "Failed to fetch sub user details.");
      }
    } catch (error) {
      notify("danger", "Failed to fetch sub user details.");
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  const handleOpenModal = (mode, data) => {
    setModal({
      isOpen: true,
      mode,
      data,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const searchString = filterState.searchName.trim();

    const newParams = {
      filters: buildFilterParams(),
      searchString,
    };
    if (!searchString && !newParams.filters.length) {
      // All filters are empty — reset the applied filter (same as Clear)
      if (JSON.stringify(filterState.filterParams) !== JSON.stringify(newParams)) {
        handleClear();
      }
      return;
    }
    if (
      JSON.stringify(filterState.filterParams) === JSON.stringify(newParams)
    ) {
      return;
    }

    setSubUserListState((prev) => ({
      ...prev,
      isLoading: true,
      error: "",
      noRecords: false,
    }));
    setShowLoaderOnClick(true);
    setFilterState((prev) => ({
      ...prev,
      filterParams: newParams,
    }));

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleClear = () => {
    setSubUserListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    setFilterState({
      searchName: "",
      searchStatus: null,
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };
  const handleStatusConfirm = async (id, newStatus) => {
    const payload = { status: newStatus };

    const res = await updateSubUser(id, payload);

    if (res.code === 200) {
      notify("success", `Sub user status updated to ${newStatus}!`);
      getSubUserList(dispatch, customerId, pageNum, filterState.filterParams);
    } else {
      notify("danger", "Failed to update sub user status");
    }
  };

  const handleStatusChange = (item) => {
    const isActive = item.status === "ACTIVE";
    const newStatus = isActive ? "SUSPENDED" : "ACTIVE";
    showAlert({
      title: `Are you sure you want to ${isActive ? "suspend" : "activate"
        } this sub user?`,
      message: `Sub user "${item.firstName} ${item.lastName}" will be ${isActive ? "suspended" : "activated"
        }.`,
      type: "warning",
      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => handleStatusConfirm(item.ID, newStatus),
      onCancel: () => { },
    });
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const buildFilterParams = () => {
    const filterParams = [];
    if (filterState.searchStatus) {
      const backendStatus =
        filterState.searchStatus.value === "DEACTIVATE"
          ? "SUSPENDED"
          : filterState.searchStatus.value;

      filterParams.push({
        field: "UserStatus",
        condition: "eq",
        filterValues: [backendStatus],
      });
    }
    return filterParams;
  };
  return (
    <>
      <div className="d-flex justify-content-end mb-1">
        <Button
          color="default"
          size="sm"
          onClick={() => handleOpenModal("Add", null)}
        >
          <i className="fa fa-plus mr-2" />
          Add Sub User
        </Button>
      </div>

      <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
        <div className="d-flex flex-wrap align-items-center filter-group">
          <Input
            type="text"
            placeholder="Search by name, email or contact number"
            className="mr-2 mb-1 filter-input-large"
            value={filterState.searchName}
            onChange={(e) =>
              setFilterState((prev) => ({
                ...prev,
                searchName: e.target.value,
              }))
            }
            onKeyDown={handleKeyDown}
            maxLength={150}
          />

          <Select
            className="mr-2 mb-2 filter-select"
            options={statusOptions}
            placeholder="Status"
            value={filterState.searchStatus}
            onChange={(selected) =>
              setFilterState((prev) => ({
                ...prev,
                searchStatus: selected,
              }))
            }
            classNamePrefix="react-select"
            styles={fixedWidthCustomStyle()}
            components={{ Option, SingleValue }}
            onKeyDown={handleKeyDown}
          />

          <Button
            color="default"
            className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
            onClick={handleSearch}
          >
            {showLoaderOnClick ? (
              <i className="fa fa-spinner fa-spin search-loader" />
            ) : (
              <>
                <i className="fa fa-search mr-1" />
                Search
              </>
            )}
          </Button>

          {(filterState.filterParams?.filters?.length > 0 ||
            filterState.filterParams?.searchString?.trim()) && (
              <Button
                color="default"
                className="mb-2 filter-btn btn-simple mt-0"
                onClick={handleClear}
              >
                <i className="fa fa-times mr-1" /> Clear
              </Button>
            )}
        </div>
      </div>

      {/* total and filter records */}
      <div className="mt-2 mb-3 mx-1">
        {subUserListData?.data?.totalRecords ? (
          !(
            filterState.filterParams?.filters?.length ||
            filterState.filterParams?.searchString
          ) ? (
            <span className="font-weight-bold">
              Total Sub Users: {subUserListData.data.totalRecords}
            </span>
          ) : (
            <span className="font-weight-bold">
              Showing {subUserListData.data.totalFilteredRecords} of{" "}
              {subUserListData.data.totalRecords} Sub Users
            </span>
          )
        ) : null}
      </div>

      <div className="table-responsive">
        {subUserListState.isLoading ? (
          <div className="loaderContainer">
            <i className="fa fa-spinner fa-spin fa-2xl loader" />
          </div>
        ) : subUserListState.error ? (
          <div className="loaderContainer text-danger">
            <h4 className="text-muted">Failed to load sub user list</h4>
            {subUserListState.error}
          </div>
        ) : subUserListState.noRecords ? (
          <div className="loaderContainer text-muted">
            No sub users added yet.
          </div>
        ) : (
          <CommonTable
            data={formattedSubUserList || []}
            resizable={false}
            defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
            columns={columns}
            showPaginationBottom={true}
            activePage={pageNum}
            totalPages={Math.ceil(
              subUserListData?.data?.totalFilteredRecords /
              subUserListData?.data?.noOfRecordsPerPage ||
              DEFAULT_RECORDS_PER_PAGE,
            )}
            className="-striped -highlight table-hover"
            setPageNum={setPageNum}
            rowClick={(rowData) => {
              const url = `/admin/customers/${urlEncodedId(customerId)}/details/subUser-details/${urlEncodedId(rowData.ID)}`;
              window.open(url, "_blank");
            }}
          />
        )}
      </div>
      <RightPanelCard isOpen={modal.isOpen} onClose={handleCloseModal}>
        <Card className="mb-0">
          <div className="d-flex justify-content-end">
            <i
              className="fa fa-close text-muted pointer"
              onClick={handleCloseModal}
            />
          </div>

          <CardHeader
            className="d-flex align-items-center justify-content-between pt-0 mb-2"
            tag="h4"
          >
            <span>{modal.mode} Sub User</span>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md="6">
                <FormGroup
                  className={`has-label ${subUserFormData.firstName.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    First Name <span className="validation-error">*</span>
                  </Label>
                  <Input
                    name="firstName"
                    type="text"
                    placeholder="Enter first name"
                    autoComplete="off"
                    value={subUserFormData.firstName.value}
                    onChange={(e) =>
                      handleInputChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    required
                    maxLength={50}
                  />
                  {subUserFormData.firstName.error && (
                    <small className="validation-error">
                      {subUserFormData.firstName.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup
                  className={`has-label ${subUserFormData.lastName.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Last Name <span className="validation-error">*</span>
                  </Label>
                  <Input
                    name="lastName"
                    type="text"
                    placeholder="Enter last name"
                    autoComplete="off"
                    value={subUserFormData.lastName.value}
                    onChange={(e) =>
                      handleInputChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    required
                    maxLength={50}
                  />
                  {subUserFormData.lastName.error && (
                    <small className="validation-error">
                      {subUserFormData.lastName.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup
                  className={`has-label ${subUserFormData.email.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label for="email">
                    Email Address <span className="validation-error">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter email address"
                    autoComplete="off"
                    value={subUserFormData.email.value}
                    onChange={(e) =>
                      handleInputChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    required
                    maxLength={100}
                  />
                  {subUserFormData.email.error && (
                    <small className="validation-error">
                      {subUserFormData.email.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup
                  className={`has-label ${subUserFormData.contact.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Contact Number <span className="validation-error">*</span>
                  </Label>
                  <InputGroup className="input-group">
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText className="d-flex align-items-center">
                        <span className="mr-1">🇸🇦</span> +966
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      name="contact"
                      type="text"
                      placeholder="Enter contact number"
                      autoComplete="off"
                      value={subUserFormData.contact.value}
                      onChange={(e) =>
                        handleInputChange(e.target.name, e.target.value)
                      }
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                      required
                      maxLength={9}
                    />
                  </InputGroup>
                  {subUserFormData.contact.error && (
                    <small className="validation-error">
                      {subUserFormData.contact.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              {modal.mode !== "Edit" && (
                <Col md="6">
                  <FormGroup
                    className={`has-label ${subUserFormData.password.error ? "has-danger" : ""
                      } d-flex flex-column align-items-start`}
                  >
                    <Label for="password">
                      Password <span className="validation-error">*</span>
                    </Label>

                    <InputGroup className="input-group">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="off"
                        placeholder="Enter password"
                        value={subUserFormData.password.value}
                        onChange={(e) =>
                          handleInputChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        required
                        maxLength={16}
                      />

                      <InputGroupAddon addonType="append">
                        <InputGroupText
                          className="d-flex align-items-center pointer"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? (
                            <i className="fa fa-eye-slash" />
                          ) : (
                            <i className="fa fa-eye" />
                          )}
                        </InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>

                    {subUserFormData.password.error && (
                      <small className="validation-error">
                        {subUserFormData.password.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
              )}
            </Row>
          </CardBody>
          <CardFooter className="d-flex justify-content-end">
            <Button
              color="secondary"
              onClick={handleCloseModal}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              color="default"
              type="submit"
              onClick={() => {
                handleSaveClick();
              }}
              disabled={hasAnyError(subUserFormData)}
            >
              {showLoaderOnClick ? (
                <i className="fa fa-spinner fa-spin fa-lg text-white" />
              ) : (
                "Save"
              )}
            </Button>
          </CardFooter>
        </Card>
      </RightPanelCard>
    </>
  );
}

export default SubUsers;
